import React, { useEffect, useMemo, useState } from 'react';
import { serviceAnalyticsService } from '../services/api';
import { ServiceAnalyticsSummary, ServiceIndustry } from '../types';
import { DEMO_ANALYTICS } from '../data/serviceAnalyticsDemo';
import ScopeFilter, { ScopeSelection } from './ScopeFilter';

// Customer-service analytics over the conversations the platform already
// holds: how long calls run, how much effort they cost the customer, how they
// end, and what industry they are actually about.
//
// Two decisions shape this page more than anything else.
//
// FIRST, it says what it cannot measure, on the page, above the charts. The
// platform has transcripts and audio duration; it does not have CSAT scores,
// disposition codes, agent identities or queue events, and on redacted
// material it does not even have job titles. A service dashboard that shows
// eight confident tiles and stays quiet about the missing ninth is how people
// end up managing to a number that was never being measured. So the response
// carries a capabilities block — measured / proxy / unavailable — and it is
// the first thing rendered.
//
// SECOND, industry comes from the transcript, never from the label. On the
// research corpus the two disagree spectacularly: the domain called
// "medicare_inbound" holds 64% of the corpus and is 28% pet boarding. A live
// tenant's dial-plan naming has no reason to be better, so the classifier
// reads the words that were actually said.
//
// The numbers today are a demo fixture (see data/serviceAnalyticsDemo.ts).
// The page asks /api/analytics_summary.php first and only falls back; when
// that endpoint ships returning the same shape for the selected scope, this
// page goes live without an edit and the demo badge removes itself.

type View = 'overview' | 'industries' | 'quality';

// Categorical slots for the three compared industries, and an ordinal blue
// ramp for the duration bands. Both validated against the white card surface
// (#FFFFFF): worst all-pairs CVD ΔE 9.0, normal-vision ΔE 19.5. The aqua slot
// sits below 3:1 contrast, so every bar that uses it carries a direct numeric
// label and the full table view below — the documented relief for that.
const SERIES = ['#2a78d6', '#C8862F', '#1baf7a'];
const BAND_RAMP = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'];
// Ink for a label sitting ON each ramp step: dark on the two light steps.
const BAND_INK = ['#0d366b', '#0d366b', '#FFFFFF', '#FFFFFF', '#FFFFFF'];

const nf = new Intl.NumberFormat();

const mmss = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;

// The three industries the comparison charts use. Structurally different
// businesses on purpose — a retail service desk, a carrier's service line and
// an outbound enrolment floor read nothing like each other, which is the whole
// argument for cutting by industry rather than by domain.
const COMPARED = [
  'Auto dealership & service',
  'Auto & property insurance',
  'Health plan / Medicare',
];

// Those three exist in the research corpus. A live tenant has whatever its own
// conversations are about, so the comparison falls back to its three largest
// industries — the charts are about contrast between businesses, not about
// those particular three.
function pickCompared(industries: ServiceIndustry[]): ServiceIndustry[] {
  const named = COMPARED
    .map(name => industries.find(i => i.name === name))
    .filter((i): i is ServiceIndustry => Boolean(i));
  if (named.length >= 2) return named;
  return industries.slice(0, 3);
}

// An unknown direction is shown as unknown.
const pct = (v: number | null | undefined, digits = 0): string =>
  v === null || v === undefined ? '—' : `${v.toFixed(digits)}%`;

const SIGNAL_ROWS: { key: keyof ServiceIndustry['signals']; label: string; hint: string }[] = [
  { key: 'hold', label: 'Hold / "let me check"', hint: 'agent stalls to look something up' },
  { key: 'transfer', label: 'Transfer', hint: 'call handed to someone else' },
  { key: 'deferral', label: 'Deferral', hint: '"nothing I can do" / "we\'ll call you back"' },
  { key: 'empathy', label: 'Empathy', hint: '"I understand", "I hear you"' },
  { key: 'gratitude', label: 'Gratitude', hint: '"thank you so much", "very helpful"' },
  { key: 'frustration', label: 'Frustration', hint: '"frustrated", "unacceptable", "fed up"' },
  { key: 'confirmed', label: 'Confirmed', hint: '"you\'re all set", confirmation number' },
];

const SIGNAL_SCALE = 70; // % of calls; the tallest bar on the page is 62.8

const AnalyticsPage: React.FC = () => {
  const [view, setView] = useState<View>('overview');
  const [scope, setScope] = useState<ScopeSelection>({});
  const [data, setData] = useState<ServiceAnalyticsSummary | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    serviceAnalyticsService
      .summary({ ...(scope.territory ? { territory: scope.territory } : {}),
                 ...(scope.domain ? { domain: scope.domain } : {}) } as any)
      .then(live => {
        if (cancelled) return;
        setData(live);
        setIsDemo(false);
      })
      .catch(() => {
        // Expected until the endpoint ships. Fall back rather than showing an
        // error: an empty red box teaches nobody anything, and the badge below
        // says plainly where the numbers came from.
        if (cancelled) return;
        setData(DEMO_ANALYTICS);
        setIsDemo(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope.territory, scope.domain]);

  const industries = useMemo(
    () => (data?.industries || []).filter(i => !i.residual),
    [data],
  );
  const residuals = useMemo(
    () => (data?.industries || []).filter(i => i.residual),
    [data],
  );
  const compared = useMemo(() => pickCompared(industries), [industries]);

  if (loading && !data) {
    return <div className="analytics-container"><p className="small">Loading analytics…</p></div>;
  }
  if (!data) {
    return <div className="analytics-container"><p className="small">No analytics available.</p></div>;
  }

  const c = data.corpus;
  // Math.max of nothing is -Infinity, which would silently turn every bar
  // width into NaN. A scope with no drivers to show is a normal state — a new
  // territory, or one whose calls all carry the same signal.
  const driverMax = data.drivers.length
    ? Math.max(...data.drivers.map(d => Math.max(d.medianWith, d.medianWithout))) * 1.08
    : 1;

  return (
    <div className="analytics-container">
      <div className="status-header-row">
        <h2>Analytics</h2>
        {isDemo && (
          <span className="analytics-demo-badge" title="The analytics endpoint is not deployed yet — these are real measured figures from the research corpus, served from the bundle.">
            Demo data
          </span>
        )}
      </div>

      {isDemo ? (
        <p className="small analytics-scope-note">
          Experimental territory · all 11 domains · measured {data.generatedAt}.
          Full pass over {nf.format(c.callsTotal)} conversations — no sampling. Live analytics
          will follow the territory and domain you select here once{' '}
          <code className="monospace">analytics_summary.php</code> is deployed.
        </p>
      ) : (
        <ScopeFilter value={scope} onChange={setScope} />
      )}

      {data.empty && (
        <p className="analytics-caution">
          <b>Nothing analysed in this scope yet.</b> Either it holds no conversations, or their
          transcripts have not been through <code className="monospace">analytics_build.php</code>{' '}
          yet. The measures below are still listed so it is clear what would appear.
        </p>
      )}

      <div className="settings-subnav">
        <button
          className={`nav-button ${view === 'overview' ? 'active' : ''}`}
          onClick={() => setView('overview')}
        >
          Overview
        </button>
        <button
          className={`nav-button ${view === 'industries' ? 'active' : ''}`}
          onClick={() => setView('industries')}
        >
          Industries
        </button>
        <button
          className={`nav-button ${view === 'quality' ? 'active' : ''}`}
          onClick={() => setView('quality')}
        >
          What this can measure
        </button>
      </div>

      {/* ---------------------------------------------------------------- */}
      {view === 'overview' && (
        <>
          <div className="status-cards">
            <div className="status-card">
              <h4>Conversations</h4>
              <div className="status-card-value">{nf.format(c.callsUnique)}</div>
              <div className="status-card-detail">
                {nf.format(c.callsTotal)} on file, {nf.format(c.duplicates)} exact duplicates removed
              </div>
            </div>
            <div className="status-card">
              <h4>Hours of talk</h4>
              <div className="status-card-value">{nf.format(c.hours)}</div>
              <div className="status-card-detail">audio duration, not wall-clock</div>
            </div>
            <div className="status-card">
              <h4>Median handle time</h4>
              <div className="status-card-value">{mmss(c.medianSeconds)}</div>
              <div className="status-card-detail">p90 {mmss(c.p90Seconds)}</div>
            </div>
            <div className="status-card">
              <h4>Transcription confidence</h4>
              <div className="status-card-value">{c.asrConfidence === null ? '—' : c.asrConfidence.toFixed(2)}</div>
              <div className="status-card-detail">mean across all conversations</div>
            </div>
            <div className="status-card">
              <h4>Industries found</h4>
              <div className="status-card-value">{c.industries}</div>
              <div className="status-card-detail">classified from transcripts, not labels</div>
            </div>
          </div>

          <h3>What lengthens a call</h3>
          <p className="small analytics-lead">
            Median handle time for conversations carrying a signal against those without it,
            across all {nf.format(c.callsUnique)}.
          </p>
          {data.drivers.length === 0 ? (
            <div className="analytics-panel">
              <p className="small" style={{ margin: 0 }}>
                No signal in this scope has calls on both sides of it, so there is no comparison to
                draw. This is what a small or very uniform set of conversations looks like.
              </p>
            </div>
          ) : (
          <div className="analytics-panel">
            <div className="analytics-legend">
              <span><i className="analytics-swatch" style={{ background: SERIES[0] }} /> with the signal</span>
              <span><i className="analytics-swatch" style={{ background: SERIES[1] }} /> without it</span>
            </div>
            {data.drivers.map(d => {
              const gap = Math.round((d.medianWith / d.medianWithout - 1) * 100);
              return (
                <div className="analytics-driver" key={d.signal}>
                  <div className="analytics-driver-label">
                    {d.label}
                    <small>{d.sharePct.toFixed(1)}% of calls · <b>+{gap}% longer</b></small>
                  </div>
                  <div className="analytics-driver-bars">
                    <div className="analytics-bar-row">
                      <span className="analytics-track">
                        <i style={{ width: `${(d.medianWith / driverMax) * 100}%`, background: SERIES[0] }} />
                      </span>
                      <span className="analytics-bar-value">{mmss(d.medianWith)} with</span>
                    </div>
                    <div className="analytics-bar-row">
                      <span className="analytics-track">
                        <i style={{ width: `${(d.medianWithout / driverMax) * 100}%`, background: SERIES[1] }} />
                      </span>
                      <span className="analytics-bar-value">{mmss(d.medianWithout)} without</span>
                    </div>
                  </div>
                </div>
              );
            })}
            <p className="analytics-caution">
              <b>Read this carefully.</b> Confirmation language rises with call length too — long
              calls contain more of everything. Only the frustration and transfer gaps are large
              enough to survive that. This is a proxy view, not a causal model.
            </p>
          </div>
          )}
        </>
      )}

      {/* ---------------------------------------------------------------- */}
      {view === 'industries' && (
        <>
          <h3>Three industries, side by side</h3>
          <p className="small analytics-lead">
            A dealership service desk, an insurance carrier's service line, and an outbound
            health-plan enrolment floor. Percentages are the share of that industry's calls
            containing the language.
          </p>
          <div className="analytics-cards">
            {compared.map((ind, idx) => (
              <div className="status-card" key={ind.name}>
                <h4>
                  <i className="analytics-swatch" style={{ background: SERIES[idx] }} /> {ind.name}
                </h4>
                <div className="analytics-hero">
                  <span className="analytics-hero-value">{mmss(ind.medianSeconds)}</span>
                  <span className="analytics-hero-label">
                    median handle time<br />p90 {mmss(ind.p90Seconds)}
                  </span>
                </div>
                <div className="status-card-detail analytics-card-meta">
                  {nf.format(ind.calls)} calls · {pct(ind.inboundPct)} inbound · ASR{' '}
                  {ind.asrConfidence === null ? '—' : ind.asrConfidence.toFixed(2)}
                </div>
                <div className="analytics-minibars">
                  {([
                    ['hold', 'Hold / "let me check"'],
                    ['transfer', 'Transfer'],
                    ['deferral', 'Deferral to a call-back'],
                    ['frustration', 'Frustration language'],
                  ] as const).map(([key, label]) => (
                    <div className="analytics-minibar" key={key}>
                      <span className="analytics-minibar-label">{label}</span>
                      <span className="analytics-track">
                        <i style={{ width: `${ind.signals[key]}%`, background: SERIES[idx] }} />
                      </span>
                      <span className="analytics-bar-value">{ind.signals[key].toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <h3>Effort, tone, outcome</h3>
          <p className="small analytics-lead">
            The same signals across the three. Read the top group as customer effort, the middle
            as tone, the last as how the call ends. Scale 0–{SIGNAL_SCALE}% of calls.
          </p>
          <div className="analytics-panel">
            <div className="analytics-legend">
              {compared.map((ind, idx) => (
                <span key={ind.name}>
                  <i className="analytics-swatch" style={{ background: SERIES[idx] }} /> {ind.name}
                </span>
              ))}
            </div>
            {SIGNAL_ROWS.map(row => (
              <div className="analytics-group" key={row.key}>
                <div className="analytics-group-label">
                  {row.label}
                  <small>{row.hint}</small>
                </div>
                <div className="analytics-group-bars">
                  {compared.map((ind, idx) => (
                    <div className="analytics-bar-row" key={ind.name}
                         title={`${ind.name} — ${row.label}: ${ind.signals[row.key].toFixed(1)}% of ${nf.format(ind.calls)} calls`}>
                      <span className="analytics-track">
                        <i style={{ width: `${(ind.signals[row.key] / SIGNAL_SCALE) * 100}%`, background: SERIES[idx] }} />
                      </span>
                      <span className="analytics-bar-value">{ind.signals[row.key].toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <h3>Where the time goes</h3>
          <p className="small analytics-lead">
            Share of each industry's calls by length. The fat tail is what drives staffing cost.
          </p>
          <div className="analytics-panel">
            <div className="analytics-legend">
              {data.bands.map((b, i) => (
                <span key={b}>
                  <i className="analytics-swatch" style={{ background: BAND_RAMP[i] }} /> {b}
                </span>
              ))}
            </div>
            {industries.map(ind => (
              <div className="analytics-stackrow" key={ind.name}>
                <span className="analytics-stack-label">{ind.name}</span>
                <span className="analytics-stack">
                  {ind.durationBands.map((pct, i) => (
                    pct < 0.05 ? null : (
                      <i
                        key={i}
                        style={{ flex: pct, background: BAND_RAMP[i], color: BAND_INK[i] }}
                        title={`${ind.name} — ${data.bands[i]}: ${pct.toFixed(1)}% of calls`}
                      >
                        {pct >= 11 ? `${pct.toFixed(0)}%` : ''}
                      </i>
                    )
                  ))}
                </span>
                <span className="analytics-bar-value">{mmss(ind.medianSeconds)}</span>
              </div>
            ))}
          </div>

          <h3>All industries</h3>
          <p className="small analytics-lead">
            The table view. "Mixed" scores on two industries at once and "Unclassified" carries too
            little vocabulary to call either way — both are residuals, not businesses, and are
            greyed accordingly.
          </p>
          <div className="table-container">
            <table className="wide-table">
              <thead>
                <tr>
                  <th>Industry (from content)</th>
                  <th>Calls</th><th>Inbound</th><th>Median</th><th>p90</th><th>Words/min</th>
                  <th>Hold</th><th>Transfer</th><th>Empathy</th><th>Gratitude</th>
                  <th>Frustration</th><th>Confirmed</th><th>Deferred</th><th>Disclosure</th><th>ASR</th>
                </tr>
              </thead>
              <tbody>
                {[...industries, ...residuals].map(ind => (
                  <tr key={ind.name} className={ind.residual ? 'analytics-residual' : undefined}>
                    <td>{ind.name}</td>
                    <td className="monospace">{nf.format(ind.calls)}</td>
                    <td className="monospace">{pct(ind.inboundPct)}</td>
                    <td className="monospace">{mmss(ind.medianSeconds)}</td>
                    <td className="monospace">{mmss(ind.p90Seconds)}</td>
                    <td className="monospace">{ind.wordsPerMinute}</td>
                    <td className="monospace">{ind.signals.hold.toFixed(1)}%</td>
                    <td className="monospace">{ind.signals.transfer.toFixed(1)}%</td>
                    <td className="monospace">{ind.signals.empathy.toFixed(1)}%</td>
                    <td className="monospace">{ind.signals.gratitude.toFixed(1)}%</td>
                    <td className="monospace">{ind.signals.frustration.toFixed(1)}%</td>
                    <td className="monospace">{ind.signals.confirmed.toFixed(1)}%</td>
                    <td className="monospace">{ind.signals.deferral.toFixed(1)}%</td>
                    <td className="monospace">{ind.signals.disclosure.toFixed(1)}%</td>
                    <td className="monospace">{ind.asrConfidence === null ? '—' : ind.asrConfidence.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ---------------------------------------------------------------- */}
      {view === 'quality' && (
        <>
          <h3>What this page can and cannot measure</h3>
          <p className="small analytics-lead">
            Everything here is derived from transcript text and audio duration. Where a measure
            needs something the platform does not hold, it says so rather than substituting a
            number that looks like it. This list is a property of the material in scope — redacted
            research transcripts can support less than a tenant's own recordings can — so it
            changes when you change territory.
          </p>
          <div className="analytics-cards">
            <div className="status-card">
              <h4><span className="analytics-chip ok">Measured</span></h4>
              <ul className="analytics-caps">
                {data.capabilities.measured.map(cap => (
                  <li key={cap.title}><b>{cap.title}</b> — {cap.detail}</li>
                ))}
              </ul>
            </div>
            <div className="status-card">
              <h4><span className="analytics-chip proxy">Proxy only</span></h4>
              <ul className="analytics-caps">
                {data.capabilities.proxy.map(cap => (
                  <li key={cap.title}><b>{cap.title}</b> — {cap.detail}</li>
                ))}
              </ul>
            </div>
            <div className="status-card">
              <h4><span className="analytics-chip no">Not available</span></h4>
              <ul className="analytics-caps">
                {data.capabilities.unavailable.map(cap => (
                  <li key={cap.title}><b>{cap.title}</b> — {cap.detail}</li>
                ))}
              </ul>
            </div>
          </div>

          <h3>Labels do not describe their contents</h3>
          <p className="small analytics-lead">
            How each domain's conversations actually distribute across industries — the evidence
            for classifying from the transcript rather than from the label.{' '}
            {data.labelMixHighlight ? (
              <>
                This is the one chart on the page drawn <b>across the whole territory</b> rather
                than the domain you selected: a label compared against its own contents is not a
                comparison.{' '}
                <code className="monospace">{data.labelMixHighlight}</code> is marked below.
              </>
            ) : (
              <>Compared across every domain you can see.</>
            )}
          </p>
          <div className="analytics-panel">
            {data.labelMix.length === 0 && (
              <p className="small" style={{ margin: '0 0 8px 0' }}>
                Only one domain is visible to your account, so there is nothing to compare labels
                against.
              </p>
            )}
            {data.labelMix.map(m => (
              <div
                className={`analytics-mixrow${m.label === data.labelMixHighlight ? ' analytics-mixrow-selected' : ''}`}
                key={m.label}
              >
                <div className="analytics-mix-label">
                  <code className="monospace">{m.label}</code>
                  <small>
                    {nf.format(m.calls)} calls
                    {m.label === data.labelMixHighlight ? ' · selected' : ''}
                  </small>
                </div>
                <div>
                  <span className="analytics-stack">
                    {m.parts.map((p, i) => (
                      <i
                        key={p.name}
                        style={{
                          flex: p.pct,
                          background: BAND_RAMP[Math.min(i, BAND_RAMP.length - 1)],
                          color: BAND_INK[Math.min(i, BAND_INK.length - 1)],
                        }}
                        title={`${m.label} — ${p.name}: ${p.pct.toFixed(1)}%`}
                      >
                        {p.pct >= 14 ? `${p.name} ${p.pct.toFixed(0)}%` : ''}
                      </i>
                    ))}
                  </span>
                  <div className="analytics-mix-legend">
                    {m.parts.map(p => `${p.name} ${p.pct.toFixed(0)}%`).join(' · ')}
                  </div>
                </div>
              </div>
            ))}
            <p className="analytics-caution">
              <b>Signals are phrase counts, not judgements.</b> "Frustration" means frustration
              vocabulary was used out loud; a politely furious caller is invisible. Treat every
              percentage on this page as a floor.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;
